// [VIEW-SLIDE 2026-10-08, user] ONE slide for the app's view toggles — Stats / Payout Details, Dialing Report PM / Sales,
// My Sales Leads to Sales / Overall Sales, Sales Scoreboard range / Monthly, PM Performance teams — built on the 0.42 s
// emphasized slide that EOM / Dashboard and Refunds / Disputes already use, so every toggle in the app moves alike.
// The user then asked for more than a plain left/right move ("add more animation which should look good"), so:
//   • depth — the leaving content dims (opacity 0.45) and settles back very slightly (scale 0.98) as it slides out; the
//     arriving content brightens and settles forward into place;
//   • cascade — on a SWITCH (never on first load) the arriving view's table rows, and any element marked
//     data-slide-item (tiles, cards), rise in one after another: 6 px + fade. [FLUID-5 2026-10-12, user] Rows: EVERY row
//     on screen, top to bottom, as one wave (components/Fluid.tsx landRows — "it should be for the complete page, not
//     for the upper half only"; it was the first 14 rows staggered and the rest arriving as one block). Tiles / cards:
//     18 ms apart, the first 14 staggered. Fill mode "backwards": once a row has landed the animation lets go of it
//     entirely, so row hover / selection styling is untouched. The wave runs once, on the rows present at the switch:
//     rows that come LATER (data landing after a skeleton, a month change) rise only through the view's own landing —
//     DataTable, <Reveal $rows>, or a page's useRowsLand (components/Fluid.tsx). A new view with its own table needs one.
//   • reduced motion — no slide, no fade, no cascade: an instant switch.
//
// Usage: wrap the part BELOW the toggle (never the toggle itself) in <SlideViewport>, and each view in
// <SlidePane $pos={slidePos(view, 'thisView', ORDER)}>, with BOTH panes kept mounted. Picking a view to the RIGHT slides
// the current one out to the left while the new one comes in from the right; picking one to the LEFT reverses it.
// The pane on show is in flow (the page takes its height); the others sit absolutely in the same spot, off to their
// side, and hide once they have slid out (visibility, so nothing in them can be tabbed into or read out).
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { easing, fluid, reducedMotion } from '../styles/motion';
import { landRows, SWITCH_WAVE } from './Fluid'; // [FLUID-5 2026-10-12] [FLUID-6 2026-10-12] SWITCH_WAVE

/** The app's view-toggle slide (EOMPage Panel, RefundsDisputesPage). The leaving pane hides only after it. */
export const SLIDE_S = '0.42s';

export type SlidePos = 'center' | 'left' | 'right';

/** Where `view`'s pane sits while `active` is on show: centre, or the side it sits on in the toggle. */
export function slidePos<T extends string>(active: T, view: T, order: readonly T[]): SlidePos {
  if (active === view) return 'center';
  return order.indexOf(view) < order.indexOf(active) ? 'left' : 'right';
}

/* clip, NOT hidden (hidden would make this a scroll box and break sticky headers), and on BOTH axes so a taller view
   sliding out cannot stretch the page's scroll height — the EOMPage Viewport lesson. */
export const SlideViewport = styled.div`
  position: relative;
  overflow: clip;
`;

/* The cascade. Delays are written per row with nth-child, so it costs no JavaScript per row. */
const CASCADE_ITEMS = 14;
const CASCADE_START_MS = 0; // [FLUID-8 2026-10-12] soft tiles start with the slide (was 90)
const CASCADE_STEP_MS = 18;
/** The cascade switches itself OFF this long after a pane arrives — after the last tile has landed (90 + 14x18 ms
 *  delay + 340 ms rise = 682 ms; the row wave: 90 + 260 + 340 = 690 ms) — so content that arrives LATER (data landing
 *  after a skeleton, a month change, a save) gets its own landing instead. */
const CASCADE_DONE_MS = 800;

// [FLUID-8 2026-10-12, user] Soft: a tile starts 35 % visible (was from nothing) — readable as the view slides in.
const riseIn = keyframes`
  from { opacity: 0.35; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

/* Tiles / cards only — table rows take the row wave (landRows, below). */
const cascade = css`
  & [data-slide-item] {
    animation: ${riseIn} 0.34s ${easing.emphasized} backwards;
    animation-delay: ${CASCADE_START_MS + CASCADE_ITEMS * CASCADE_STEP_MS}ms;
  }
  ${Array.from({ length: CASCADE_ITEMS }, (_, i) => `
  & [data-slide-item]:nth-child(${i + 1}) { animation-delay: ${CASCADE_START_MS + i * CASCADE_STEP_MS}ms; }`).join('')}
`;

const Pane = styled.div<{ $pos: SlidePos; $cascade: boolean; $flat: boolean; $nested: boolean }>`
  position: ${(p) => (p.$pos === 'center' ? 'relative' : 'absolute')};
  top: 0;
  left: 0;
  right: 0;
  /* none (not translateX(0)) at rest, so a resting pane is no containing block for fixed content. Settles from the top
     centre, so the top edge stays level with the toggle while it moves. $flat: no settle-back, no dimming. */
  transform: ${(p) => (p.$pos === 'center' ? 'none' : `translateX(${p.$pos === 'left' ? -100 : 100}%)${p.$flat ? '' : ' scale(0.98)'}`)};
  transform-origin: 50% 0;
  opacity: ${(p) => (p.$pos === 'center' || p.$flat ? 1 : 0.45)};
  visibility: ${(p) => (p.$pos === 'center' ? (p.$nested ? 'inherit' : 'visible') : 'hidden')};
  transition: transform ${SLIDE_S} ${easing.emphasized}, opacity ${SLIDE_S} ${easing.standard},
    visibility 0s linear ${(p) => (p.$pos === 'center' ? '0s' : SLIDE_S)};

  ${(p) => (p.$cascade ? cascade : '')}

  ${reducedMotion} {
    transition: none;
    & [data-slide-item] { animation: none; }
  }
`;

/** [FLUID-6 2026-10-12, user] `$quiet` (default false): the pane only SLIDES on a switch — no row wave, no tile cascade.
 *  Stats and the Sales Scoreboard pass it: "the first time someone comes on the tab it shows the animation for the data,
 *  and then in the toggle switch change it will not show an animation" — their tables and tiles rise in when their data
 *  lands (the page's own landing), never because the pill was pressed. */
/** [LEAD-ASSIGNMENT 2026-10-13, user] `$flat` (default false): a plain sideways slide — the panes keep full strength and
 *  size while they move, no dimming, no settle-back. Lead Assignment passes it with `$quiet`: "remove the table fade
 *  animation from this, it will just smoothly work for the left and right". Every other toggle is unchanged. */
/** [LGP-NESTED 2026-10-15] `$nested` (default false): a pane inside ANOTHER toggle's pane (Lead Generator Performance: the US | ROW
 *  tables inside the Leads Count view, Overall | US | ROW inside Monthly Performance). On show it takes its parent's visibility
 *  (inherit) instead of forcing `visible` — a forced `visible` beats the hidden outer pane, so the inner table stayed drawn (and
 *  reachable by Tab) off-screen while another view was on show. Every pane without it is unchanged. */
type SlidePaneProps = { $pos: SlidePos; $quiet?: boolean; $flat?: boolean; $nested?: boolean } & React.HTMLAttributes<HTMLDivElement>;

/** One view of a toggle. The cascade runs only when this pane ARRIVES by a switch — not on the page's first render. */
export const SlidePane = React.forwardRef<HTMLDivElement, SlidePaneProps>(({ $pos, $quiet = false, $flat = false, $nested = false, ...rest }, ref) => {
  // Decided during render, not in an effect: an effect would paint the rows once at full strength and then snap them
  // to the cascade's first frame (a flash). Idempotent, so a double render (StrictMode) decides the same way.
  const prev = useRef($pos);
  const arrived = useRef(false);
  const [, settle] = useState(0);
  if (prev.current !== $pos) {
    arrived.current = $pos === 'center';
    prev.current = $pos;
  }
  // [FLUID-5 2026-10-12, user] The arriving pane's table rows: every row on screen, top to bottom, as one wave (rising
  // 0.34 s each, starting 90 ms in, as the cascade did). Before paint, so no row shows at rest first.
  const own = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    own.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  useLayoutEffect(() => {
    if (!arrived.current || $quiet) return undefined;
    // [FLUID-6 2026-10-12, user] "it feels laggy": the light switch wave — rows 40 % visible as the slide starts, all in
    // place as it ends (components/Fluid.tsx SWITCH_WAVE). Was: from nothing, 90 ms in, 0.34 s each.
    return landRows(own.current, 0, fluid.riseMs, SWITCH_WAVE);
  }, [$pos, $quiet]);
  // Once the arriving rows have landed, drop the cascade (fill mode backwards: nothing visible changes).
  useEffect(() => {
    if (!arrived.current) return undefined;
    const t = window.setTimeout(() => { arrived.current = false; settle((n) => n + 1); }, CASCADE_DONE_MS);
    return () => window.clearTimeout(t);
  }, [$pos]);
  return <Pane ref={setRef} $pos={$pos} $cascade={arrived.current && !$quiet} $flat={$flat} $nested={$nested} {...rest} />;
});
SlidePane.displayName = 'SlidePane';
